import { toTimestamp } from '@mi-front/core';
import * as d3 from 'd3';
import { useEffect, useRef } from 'react';
import { YoutubeViewersPoint } from '../utils/youtube-stats-waveform-types';

function formatViewerValue(viewers: number) {
  if (viewers < 1000) {
    return viewers.toLocaleString();
  }

  return `${(viewers / 1000).toFixed(1)}k`;
}

interface YoutubeStatsWaveformGraphV2SvgProps {
  width: number;
  height: number;
  scale: (ts: number) => number | undefined;
  rangeFrom: number;
  stepMinutes: number;
  processedData: YoutubeViewersPoint[];
}

/**
 * Draws the v2 YouTube viewers overlay SVG and always-visible values over each dot.
 */
export function YoutubeStatsWaveformGraphV2Svg(props: YoutubeStatsWaveformGraphV2SvgProps) {
  const { width, height, scale, rangeFrom, stepMinutes, processedData } = props;
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || !processedData.length) {
      return;
    }

    if (processedData.every((point) => point.viewers === 0)) {
      return;
    }

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const minViewers = d3.min(processedData, (point) => point.viewers) ?? 0;
    const maxViewers = d3.max(processedData, (point) => point.viewers) ?? 0;
    const yDomainMin = minViewers === maxViewers ? Math.max(minViewers - 1, 0) : minViewers;
    const yDomainMax = minViewers === maxViewers ? maxViewers + 1 : maxViewers;

    const yScale = d3
      .scaleLinear()
      .domain([yDomainMin, yDomainMax])
      .range([height - 20, 20]);
    const xOffset = scale(toTimestamp(rangeFrom)) || 0;
    const adjustedX = (x: number) => x - xOffset;
    const getX = (point: YoutubeViewersPoint) => scale(point.timestamp) || 0;

    const line = d3
      .line<YoutubeViewersPoint>()
      .x((point) => adjustedX(getX(point)))
      .y((point) => yScale(point.viewers) || 0)
      .curve(d3.curveMonotoneX);

    svg
      .append('path')
      .datum(processedData)
      .attr('fill', 'none')
      .attr('stroke', '#ff6384')
      .attr('stroke-width', 2)
      .attr('d', line);

    const minDotSpacingMs = Math.max(stepMinutes, 1) * 60 * 1000;
    const pointsForMarkers = processedData.reduce<YoutubeViewersPoint[]>((acc, point) => {
      if (!acc.length) {
        acc.push(point);
        return acc;
      }

      const lastPoint = acc[acc.length - 1];
      if (point.timestamp - lastPoint.timestamp >= minDotSpacingMs) {
        acc.push(point);
      }

      return acc;
    }, []);

    svg
      .selectAll('.dot')
      .data(pointsForMarkers)
      .enter()
      .append('circle')
      .attr('class', 'dot')
      .attr('cx', (point) => adjustedX(getX(point)))
      .attr('cy', (point) => yScale(point.viewers) || 0)
      .attr('r', 4)
      .attr('fill', '#ff6384')
      .attr('stroke', 'white')
      .attr('stroke-width', 2);

    svg
      .selectAll('.viewer-label')
      .data(pointsForMarkers)
      .enter()
      .append('text')
      .attr('class', 'viewer-label')
      .attr('x', (point) => adjustedX(getX(point)))
      .attr('y', (point) => (yScale(point.viewers) || 0) - 8)
      .attr('text-anchor', 'middle')
      .attr('fill', '#f38ca6')
      .style('font-size', '10px')
      .style('font-family', 'Arial, sans-serif')
      .style('font-weight', '400')
      .style('pointer-events', 'none')
      .text((point) => formatViewerValue(point.viewers));
  }, [height, processedData, rangeFrom, scale, stepMinutes]);

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'none',
      }}
    />
  );
}
